/**
 * @module confirmSyncQB/columnSort/sortFields
 * @description 按 panel 的 `column_sort` 重排 QB 的 `columnDimensions` / `columnMetrics`
 *
 * 对齐旧版 `sortFieldsByColumnSort` 行为，供 Confirm 写回 QB 使用。
 *
 * ## 设计要点
 * 1. **键匹配**：维/指通过 `getColumnSortKey`（table_code + table_id + column + builder_type）
 *    与 column_sort 项对齐；指标侧需 `buildMetricDisplayInfo` 处理与维度同名的展示名。
 * 2. **不参与 QB 物理重排的 column_sort 项**（仅影响面板展示顺序）：
 *    - 对照组：`DATALAB_COMPARE_USER_FILTERS`
 *    - 独立对比时间：`is_compare_date === true` 且非维度
 *    - `% ` 虚拟列：若主指标已在 sort 中，则跳过 % 项，避免重复推 metric key
 * 3. **稳定尾部**：sort 中未出现的 QB 字段按原 QB 数组顺序追加到末尾，
 *    避免 QB 新增字段在 Confirm 后消失。
 */

import { SortableType } from '@dbl/core/chartEditor';
import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import {
  buildMetricDisplayInfo,
  getFieldIdentityKey,
} from '../../linkage/qbFields/qbLinkage/handlers/columnSort/untils/fieldUtils';
import {
  getColumnSortKey,
  isColumnSortDimension,
  isCompareUserFilterColumn,
  PERCENTAGE_PREFIX,
} from '../../linkage/qbFields/qbLinkage/handlers/columnSort/untils/keys';
import type {
  ISortFieldsByColumnSortInput,
  ISortFieldsByColumnSortResult,
} from './types';

/**
 * 对比时间列在 column_sort 里可能是 Metric 类型且带 `is_compare_date`；
 * 这类列只参与图表/面板排序，不对应 QB columnDimensions 槽位，重排时跳过。
 */
function isStandaloneCompareTimeColumn(item: IColumnSortItem): boolean {
  if (isColumnSortDimension(item)) {
    return false;
  }
  return item.is_compare_date === true;
}

/** 从 % 虚拟列名还原主指标名（兼容 `% ` 与裸 `%` 前缀） */
function stripMetricPrefix(column: string): string {
  if (column.startsWith(PERCENTAGE_PREFIX)) {
    return column.slice(PERCENTAGE_PREFIX.length).trim();
  }
  if (column.startsWith('%')) {
    return column.slice(1).trim();
  }
  return column;
}

/**
 * 根据 `column_sort` 顺序重排 QB 列维/列指数组。
 *
 * @param input.columnSort - Confirm 合并后的 column_sort
 * @param input.columnDimensions - 同步前 QB 列维（来自 useSettingsDraft 入参）
 * @param input.columnMetrics - 同步前 QB 列指
 */
export function sortFieldsByColumnSort({
  columnSort,
  columnDimensions,
  columnMetrics,
}: ISortFieldsByColumnSortInput): ISortFieldsByColumnSortResult {
  if (!Array.isArray(columnSort) || columnSort.length === 0) {
    return {
      sortedColumnDimensions: columnDimensions,
      sortedColumnMetrics: columnMetrics,
    };
  }

  // ===== 1) 建立 QB 字段 → sortKey 索引 =====
  const dimensionMap = new Map<string, unknown>();
  const dimensionKeySet = new Set<string>();
  columnDimensions.forEach(raw => {
    const row = raw as Record<string, unknown>;
    const key = getColumnSortKey({
      table_code: String(row.table_code ?? 't0'),
      table_id: String(row.table_id ?? ''),
      column: String(row.column_name ?? row.column ?? ''),
      builder_type: SortableType.Dimension,
    });
    dimensionMap.set(key, raw);
    dimensionKeySet.add(
      getFieldIdentityKey({
        table_code: String(row.table_code ?? 't0'),
        table_id: String(row.table_id ?? ''),
        column: String(row.column_name ?? row.column ?? ''),
      }),
    );
  });

  const metricMap = new Map<string, unknown>();
  columnMetrics.forEach(raw => {
    const row = raw as Record<string, unknown>;
    const hasDuplicate = dimensionKeySet.has(getFieldIdentityKey(row));
    const metricDisplayInfo = buildMetricDisplayInfo(
      {
        column_name: String(row.column_name ?? row.column ?? ''),
        column_display: String(row.column_display ?? ''),
        column_display_en: String(row.column_display_en ?? ''),
        aggregate: row.aggregate as string | undefined,
      },
      hasDuplicate,
    );
    const key = getColumnSortKey({
      table_code: String(row.table_code ?? 't0'),
      table_id: String(row.table_id ?? ''),
      column: metricDisplayInfo.column,
      builder_type: SortableType.Metric,
    });
    metricMap.set(key, raw);
  });

  // 主指标名集合：用于判断 % 虚拟列是否应单独推入 sortedMetricKeys
  const mainMetricNamesInColumnSort = new Set<string>();
  columnSort.forEach(item => {
    const col = item.column || '';
    if (
      item.builder_type === SortableType.Metric &&
      !col.startsWith(PERCENTAGE_PREFIX) &&
      !col.startsWith('%')
    ) {
      mainMetricNamesInColumnSort.add(col);
    }
  });

  const sortedMetricKeys: string[] = [];
  const seenMetricKeys = new Set<string>();

  // ===== 2) 按 column_sort 顺序收集指标 key =====
  columnSort.forEach(item => {
    if (item.builder_type !== SortableType.Metric) {
      return;
    }
    const column = item.column || '';
    const isPercentage =
      column.startsWith(PERCENTAGE_PREFIX) || column.startsWith('%');

    if (isPercentage) {
      const mainMetricName = stripMetricPrefix(column);
      // 主指标已在 sort 中则 % 行只作展示，不重复映射 QB metric
      if (mainMetricNamesInColumnSort.has(mainMetricName)) {
        return;
      }
      let matchedMetricKey: string | null = null;
      metricMap.forEach((metric, key) => {
        if (matchedMetricKey) {
          return;
        }
        const row = metric as Record<string, unknown>;
        const metricKey = getColumnSortKey({
          table_code: String(row.table_code ?? 't0'),
          table_id: String(row.table_id ?? ''),
          column: mainMetricName,
          builder_type: SortableType.Metric,
        });
        if (key === metricKey) {
          matchedMetricKey = key;
        }
      });
      if (matchedMetricKey && !seenMetricKeys.has(matchedMetricKey)) {
        sortedMetricKeys.push(matchedMetricKey);
        seenMetricKeys.add(matchedMetricKey);
      }
    } else {
      const key = getColumnSortKey(item);
      if (metricMap.has(key) && !seenMetricKeys.has(key)) {
        sortedMetricKeys.push(key);
        seenMetricKeys.add(key);
      }
    }
  });

  const sortedDimensionKeys: string[] = [];
  const seenDimensionKeys = new Set<string>();

  // ===== 3) 按 column_sort 顺序收集维度 key（跳过虚拟/对照项） =====
  columnSort.forEach(item => {
    if (item.builder_type !== SortableType.Dimension) {
      return;
    }
    if (isCompareUserFilterColumn(item.column)) {
      return;
    }
    if (isStandaloneCompareTimeColumn(item)) {
      return;
    }
    const key = getColumnSortKey(item);
    if (!dimensionMap.has(key)) {
      return;
    }
    if (!seenDimensionKeys.has(key)) {
      sortedDimensionKeys.push(key);
      seenDimensionKeys.add(key);
    }
  });

  const sortedColumnMetrics = sortedMetricKeys
    .map(key => metricMap.get(key))
    .filter((item): item is unknown => item !== undefined);

  const sortedColumnDimensions = sortedDimensionKeys
    .map(key => dimensionMap.get(key))
    .filter((item): item is unknown => item !== undefined);

  // ===== 4) 未出现在 column_sort 中的 QB 字段保持原相对顺序追加 =====
  columnMetrics.forEach(raw => {
    const row = raw as Record<string, unknown>;
    const hasDuplicate = dimensionKeySet.has(getFieldIdentityKey(row));
    const display = buildMetricDisplayInfo(
      {
        column_name: String(row.column_name ?? row.column ?? ''),
        column_display: String(row.column_display ?? ''),
        column_display_en: String(row.column_display_en ?? ''),
        aggregate: row.aggregate as string | undefined,
      },
      hasDuplicate,
    );
    const key = getColumnSortKey({
      table_code: String(row.table_code ?? 't0'),
      table_id: String(row.table_id ?? ''),
      column: display.column,
      builder_type: SortableType.Metric,
    });
    if (!seenMetricKeys.has(key)) {
      sortedColumnMetrics.push(raw);
    }
  });

  columnDimensions.forEach(raw => {
    const row = raw as Record<string, unknown>;
    const key = getColumnSortKey({
      table_code: String(row.table_code ?? 't0'),
      table_id: String(row.table_id ?? ''),
      column: String(row.column_name ?? row.column ?? ''),
      builder_type: SortableType.Dimension,
    });
    if (!seenDimensionKeys.has(key)) {
      sortedColumnDimensions.push(raw);
    }
  });

  return { sortedColumnDimensions, sortedColumnMetrics };
}
